import React, { useCallback, useMemo, useState } from "react";
import styles from "./shift-details-stock-inventory.module.scss";
import { fuelSalesColumn } from "./shift-details-stock-inventory-column";
import { Button, Text } from "components/commons";
import { formatAmount, formatVolume, truncate } from "utils";
import { find, sortBy, sumBy } from "lodash";
import Big from "big.js";
import classNames from "classnames";
import { InputAmount } from "components/field";
import { useAuth, useModal, useRouter } from "hooks";
import { EmployeePosition, FormMode, ShiftStatus } from "enums";
import locale from "localization";
import { removeComasStringAmount } from "utils/text.utils";
import AddRemarksTotalizerModal from "./fuel-variance/add-remarks-totalizer";
import { isProductExistInUGT } from "modules/shift-records/shift-records.mapper";
import { prettifyProductCode } from "utils/pretty.utils";
import TooltipBubble from "components/commons/tooltip/tooltip-bubble.module";
import { totalizerAnchorId } from "../shift-records-details-completion";

function nameToSku(name) {
  if (!name) return "";
  return name
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[^a-z0-9]/g, "");
}

const computeTotalAmount = (t, shift) => {
  let pumpDiscountVat12Percent = t.quantity * t.pumpDiscountPerItem;

  if (t.vat === 0) {
    pumpDiscountVat12Percent = pumpDiscountVat12Percent / 1.12;
  } else {
    pumpDiscountVat12Percent = 0;
  }

  const pricePerUnit =
    t.type === "fuel"
      ? shift.shift.pricing.find((p) => p.product === nameToSku(t.name))?.pricePerLiter || 0
      : t?.originalPricePerItem || 0;

  let grossTotalAmount =
    t.vat === 0
      ? t.quantity * pricePerUnit - pumpDiscountVat12Percent
      : t.grossTotalAmount - t.totalPumpDiscount;

  // from manual input
  if (Math.abs(grossTotalAmount - t.grossTotalAmount) <= 0.1) {
    grossTotalAmount =
      truncate(t.grossTotalAmount, 2) -
      (t.totalDiscount > 0 && t.discountValue > 0 ? t.totalDiscount : 0);
  }
  // let totalPumpDiscount = -(t.quantity * t.pumpDiscountPerItem);

  // if (t.vat === 0) {
  //   const pumpDiscountVatAmount = totalPumpDiscount - totalPumpDiscount / 1.12;
  //   totalPumpDiscount = totalPumpDiscount - pumpDiscountVatAmount;
  //   totalPumpDiscount += Math.abs(pumpDiscountVatAmount);
  // }

  return (
    Big(grossTotalAmount || 0)
      // .plus(totalPumpDiscount)
      .round(2)
      .toNumber()
  );
};

const computeDiscount = (t, shift) => {
  if (t.totalDiscount > 0 && t.discountValue > 0) {
    return t.totalDiscount;
  }

  const pumpNumber = Number(String(t.pump).split(" ")[1]);
  const pumpDiscount = shift.shiftDiscounts.find(
    (discount) =>
      discount.product === nameToSku(t.name) && Number(discount.pumpSlot) === pumpNumber,
  );
  const isNoDiscountForItem = t?.discount === "" && (t?.pumpDiscountPerItem ?? 0) === 0;
  // STOCQ-5886: Liters Input stores per-liter pump discount via t.pumpDiscountPerItem.
  // The previous logic only computed effectivePumpDiscount from shiftDiscounts when
  // BOTH per-item and totalPumpDiscount were zero, so Liters Input rows ended up with
  // effectivePumpDiscount=0 and the residual leaked into Discount/Premium.
  const effectivePumpDiscount = (() => {
    if ((t.pumpDiscountPerItem ?? 0) > 0) {
      const perLiter = t.vat === 0 ? t.pumpDiscountPerItem / 1.12 : t.pumpDiscountPerItem;
      return Big(perLiter)
        .times(t.quantity || 0)
        .round(2)
        .toNumber();
    }
    if ((t.totalPumpDiscount ?? 0) > 0) {
      return t.totalPumpDiscount;
    }
    if (!isNoDiscountForItem && (pumpDiscount?.discountPerLiter ?? 0) > 0) {
      return t.vat === 0
        ? Big(pumpDiscount.discountPerLiter)
            .div(1.12)
            .times(t.quantity || 0)
            .round(2)
            .toNumber()
        : Big(pumpDiscount.discountPerLiter)
            .times(t.quantity || 0)
            .round(2)
            .toNumber();
    }
    return 0;
  })();
  let totalPromoDiscount =
    t.vat === 0
      ? 0
      : (t.discount || (pumpDiscount?.discountPerLiter ?? 0) === 0) &&
          t.pumpDiscountPerItem === 0 &&
          t.totalDiscount > 0
        ? Math.max(0, t.totalDiscount - effectivePumpDiscount)
        : 0;

  totalPromoDiscount = t.discountValue < 0 ? -Math.abs(totalPromoDiscount) : totalPromoDiscount;
  // STOCQ-5883: when only a pump discount is applied, the per-item totalDiscount
  // mirrors the pump amount with a negative sign. Offset the pump portion so the
  // residual reflects only a true premium (negative discount/premium).
  const adjustedAbsPremium = Math.max(0, Math.abs(t.totalDiscount ?? 0) - effectivePumpDiscount);
  const premiumDiscount = (t.totalDiscount ?? 0) < 0 ? -adjustedAbsPremium : 0;

  return totalPromoDiscount + premiumDiscount;
};

const isTxnPremium = (t) =>
  Math.abs(t.totalDiscount ?? 0) > 0.005 &&
  (t.pumpDiscountPerItem ?? 0) === 0 &&
  (t.totalPumpDiscount ?? 0) === 0 &&
  (t.discount == null || t.discount === "") &&
  Math.abs((t.grossTotalAmount ?? 0) - (t.netTotalAmount ?? 0)) < 0.01;

export const getProductPumpsWithTransactions = ({ productCode = "", currentShift }) => {
  const transactions =
    currentShift?.transactionItem?.[`${productCode}Transactions`]?.filter((transaction) =>
      transaction.pump.includes("Pump"),
    ) || [];
  if (
    transactions.length === 0
      ? true
      : !isProductExistInUGT(productCode, currentShift?.station?.stationUgt)
  ) {
    return [];
  }

  const productCodeTransactionItem = sortBy(
    currentShift?.transactionItem?.[`${productCode}TransactionItem`] || [],
    "metadata",
  )
    .map((t) => ({
      ...t,
      productCode: productCode,
      productName: prettifyProductCode(productCode),
    }))
    .filter((t) => t.pump.includes("Pump"));

  return productCodeTransactionItem
    .filter((productCodeTransaction) => productCodeTransaction.pump !== "Pump 02")
    .map((productCodeTransaction) => {
      const pumpNumber = Number(String(productCodeTransaction.pump).split(" ")[1]);
      const pumpDiscount = currentShift.shiftDiscounts.find(
        (discount) =>
          discount.product === productCodeTransaction.productCode &&
          Number(discount.pumpSlot) === pumpNumber,
      );
      const pumpSaleTransactions = transactions
        .filter(
          (t) =>
            t.pump === productCodeTransaction.pump &&
            !t.transaction.adjustmentType &&
            t.transaction.status === "sale",
        )
        // .map((t) => {
        //   const computedTotalAmount = computeTotalAmount(t, currentShift);
        //   const computedDiscount = computeDiscount(t, currentShift);
        //   return {
        //     ...t,
        //     computedTotalAmount,
        //     computedDiscount,
        //   };
        // })
        .reduce(
          (acc, t) => {
            const computedTotalAmount = computeTotalAmount(t, currentShift);
            const computedDiscount = computeDiscount(t, currentShift);

            return {
              transactionAmount: acc.transactionAmount + t.transaction.amount,
              amount: acc.amount + t.amount,
              quantity: acc.quantity + t.quantity,
              grossTotalAmount: acc.grossTotalAmount + t.grossTotalAmount,
              netTotalAmount: acc.netTotalAmount + t.netTotalAmount,
              originalTotalAmount: acc.originalTotalAmount + t.originalTotalAmount,
              pumpDiscountPerItem: acc.pumpDiscountPerItem + t.pumpDiscountPerItem,
              totalPumpDiscount: acc.totalPumpDiscount + t.totalPumpDiscount,
              totalDiscount: acc.totalDiscount + t.totalDiscount,
              // STOCQ-5883: VAT-exempt portion only exists for VAT-exempt items (t.vat === 0).
              // Use |totalDiscount| because raw totalDiscount can be stored with a negative sign
              // (mirroring pump discount), which would otherwise flip the deduction and produce
              // a spurious vatExempt amount for fully taxable fuel.
              vatExemptAmount:
                acc.vatExemptAmount +
                (t.vat === 0 &&
                t.originalTotalAmount - Math.abs(t.totalDiscount) - t.netTotalAmount > 0.005
                  ? -(t.originalTotalAmount - Math.abs(t.totalDiscount) - t.netTotalAmount)
                  : 0),
              // STOCQ-5847: propagate vat + vatExempt so the rolled-up pump item still answers
              // isTaxVatExemptItem(item) correctly. Sum is fine — taxable sub-rows have vat > 0
              // and vatExempt = 0; Tax-VAT-Exempt sub-rows have vat = 0 and vatExempt > 0.
              vat: acc.vat + (t.vat || 0),
              vatExempt: acc.vatExempt + (t.vatExempt || 0),
              vatExempt12percent: acc.vatExempt12percent + t.vatExempt12percent,
              discountValue: acc.discountValue + t.discountValue,

              //computed
              computedTotalAmount: acc.computedTotalAmount + computedTotalAmount,
              computedDiscount: acc.computedDiscount + computedDiscount,
              computedPremium:
                acc.computedPremium + (isTxnPremium(t) ? Math.abs(t.totalDiscount || 0) : 0),
              computedDiscountOnly:
                acc.computedDiscountOnly + (isTxnPremium(t) ? 0 : computedDiscount),
            };
          },
          {
            transactionAmount: 0,
            amount: 0,
            quantity: 0,
            grossTotalAmount: 0,
            netTotalAmount: 0,
            originalTotalAmount: 0,
            pumpDiscountPerItem: 0,
            totalPumpDiscount: 0,
            totalDiscount: 0,
            vatExemptAmount: 0,
            vat: 0,
            vatExempt: 0,
            vatExempt12percent: 0,
            discountValue: 0,
            computedTotalAmount: 0,
            computedDiscount: 0,
            computedPremium: 0,
            computedDiscountOnly: 0,
          },
        );

      return {
        ...productCodeTransaction,
        transactionAmount: pumpSaleTransactions.transactionAmount,
        amount: pumpSaleTransactions.amount,
        quantity: pumpSaleTransactions.quantity,
        grossTotalAmount: pumpSaleTransactions.grossTotalAmount,
        netTotalAmount: pumpSaleTransactions.netTotalAmount,
        originalTotalAmount: pumpSaleTransactions.originalTotalAmount,
        pumpDiscountPerItem: pumpDiscount?.discountPerLiter || 0,
        isAmountInput: pumpSaleTransactions.pumpDiscountPerItem === 0,
        totalPumpDiscount: pumpSaleTransactions.totalPumpDiscount,
        totalDiscount: pumpSaleTransactions.totalDiscount,
        vatExemptAmount: pumpSaleTransactions.vatExemptAmount,
        vatExempt12percent: pumpSaleTransactions.vatExempt12percent,
        vat: pumpSaleTransactions.vat,
        vatExempt: pumpSaleTransactions.vatExempt,
        discountValue: pumpSaleTransactions.discountValue,
        //computed
        computedTotalAmount: pumpSaleTransactions.computedTotalAmount,
        computedDiscount: pumpSaleTransactions.computedDiscount,
        computedPremium: pumpSaleTransactions.computedPremium,
        computedDiscountOnly: pumpSaleTransactions.computedDiscountOnly,
      };
    });
};

const FuelSummary = ({
  shift,
  isShiftEndedCompletely,
  data,
  transactions,
  name,
  sku,
  handleOnChangePumpTotalizers,
  handleOnChangePumpRemarks,
  shiftPumpTotalsFields = [],
  form,
  // recentShiftDetails,
}) => {
  // shift = {
  //   ...shift,
  //   recentShift: null,
  // };
  transactions = transactions.filter((transaction) => transaction.pump.includes("Pump"));
  const addRemarks = useModal();
  const [remarks, setRemarks] = useState("");
  const [selectedPump, setSelectedPump] = useState({ productName: "", pump: "" });
  const { query } = useRouter();
  const pageMode = query?.pageMode;

  const isShiftStarted = shift?.shift?.status === ShiftStatus.Started;
  const isTotalizerBlank =
    shift?.shift?.status === ShiftStatus.Ended &&
    Boolean(shift?.previousShiftId) &&
    shift?.recentShift?.shiftId !== shift?.previousShiftId;

  const { accessRole } = useAuth({
    pageKey: "shift",
  });

  const isEndingDipstickEnabled = form.fields["isReadingDipstickEnding"].value === true;

  const stationPumps = useMemo(
    () => shift.shiftPumpTotals.filter((pump) => pump.fuelingPointId !== "Pump 02") || [],
    [shift],
  );
  // const isShowRemarks = useMemo(
  //   () => pageMode === FormMode.Edit || shift.shift.status === ShiftStatus.Closed,
  //   [pageMode, shift.shift.status]
  // );
  const pumpDiscounts = useMemo(() => shift.shiftDiscounts || [], [shift]);
  const productPumps = useMemo(() => {
    let currentShiftTransaction = getProductPumpsWithTransactions({
      productCode: sku,
      currentShift: shift,
    });

    const clonePumpTotalizer = currentShiftTransaction[0];

    const stationPumpsWithoutTransactions = stationPumps
      .filter(
        (pump) =>
          !currentShiftTransaction.some((pt) => pt.shiftPumpTotalId === pump.shiftPumpTotalId),
      )
      .map((pt) => {
        const beginningTotalizer = pt[`startTotalizer${name.replace(" ", "")}`];
        const endingTotalizer = pt[`endTotalizer${name.replace(" ", "")}`];
        const pumpNumber = Number(String(pt.fuelingPointId).split(" ")[1]);
        const pumpDiscount = pumpDiscounts.find(
          (discount) => discount.product === sku && Number(discount.pumpSlot) === pumpNumber,
        );

        return {
          ...clonePumpTotalizer,
          shiftPumpTotalId: pt.shiftPumpTotalId,
          beginningTotalizer: beginningTotalizer,
          endingTotalizer: endingTotalizer,
          metadata: `${pt.fuelingPointId}, ${pt.fuelingPointId}`,
          pump: pt.fuelingPointId,
          sku: sku,
          quantity: 0,
          pumpDiscountPerItem: pumpDiscount?.discountPerLiter || 0,
          amount: 0,
          totalPumpDiscount: 0,
          originalTotalAmount: 0,
          netTotalAmount: 0,
          totalDiscount: 0,
          vatExemptAmount: 0,
          vatExempt12percent: 0,
          vatExempt: 0,
          grossTotalAmount: 0,
          //computed
          computedTotalAmount: 0,
          computedDiscount: 0,
          computedPremium: 0,
          computedDiscountOnly: 0,
        };
      });

    currentShiftTransaction = [...currentShiftTransaction, ...stationPumpsWithoutTransactions];

    currentShiftTransaction = currentShiftTransaction.map((t) => {
      const pumpNumber = Number(String(t.pump).split(" ")[1]);
      return {
        ...t,
        pumpNumber,
      };
    });

    return sortBy(currentShiftTransaction, ["pumpNumber"]);
  }, [sku, shift, stationPumps, name, pumpDiscounts]);

  const getPrice = useCallback(() => {
    return find(shift.shift.pricing, { product: sku })?.pricePerLiter || 0;
  }, [shift.shift.pricing, sku]);

  // const totalAmount =
  //   sumBy(
  //     productPumps.map((t) => {
  //       // const totalPumpDiscount = t.vat === 0 ? 0 : -t.totalPumpDiscount;
  //       // const vatPumpDiscount = t.pumpDiscountPerItem * t.quantity - t.totalPumpDiscount;
  //       // const totalAmount =
  //       //   t.discount !== ""
  //       //     ? t.originalTotalAmount + totalPumpDiscount
  //       //     : t.amount + vatPumpDiscount;

  //       const totalAmount = computeTotalAmount(t);
  //       return {
  //         totalAmount: totalAmount,
  //       };
  //     }),
  //     "totalAmount"
  //   ) || 0;

  const totalAmount = sumBy(productPumps, "computedTotalAmount") || 0;

  const computeTotalDiscount = (items) => {
    return sumBy(items, "computedDiscount") || 0;
  };

  // const adjustedAbsTotalDiscount = Math.max(0, Math.abs(rawTotalDiscount) - totalPumpDiscountSum);
  // let totalDiscount = rawTotalDiscount < 0 ? -adjustedAbsTotalDiscount : adjustedAbsTotalDiscount;
  let totalDiscount = computeTotalDiscount(productPumps);
  const totalPremium = sumBy(productPumps, "computedPremium") || 0;
  const totalDiscountOnly = sumBy(productPumps, "computedDiscountOnly") || 0;
  // const totalFinalAmount = sumBy(productPumps, "netTotalAmount") || 0;

  const totalVatExempt =
    sumBy(transactions, (item) => {
      return item.vatExempt12percent;
    }) || 0;

  const totalFinalAmount = totalAmount + totalDiscount + totalVatExempt;
  const totalizerDifference = sumBy(productPumps, "totalizerDifference") || 0;
  const totalizerVariance = sumBy(productPumps, "totalizerVariance") || 0;

  // const getPaymentDetails = (itemId) => {
  //   return (
  //     shift.transactionPaymentMethod.cash.find((p) => p.transactionItemId === itemId) ||
  //     shift.transactionPaymentMethod.nonCash.find((p) => p.transactionItemId === itemId)
  //   );
  // };

  const totalizerVariancePercentage =
    totalizerDifference && totalizerDifference !== 0
      ? Number(Big(totalizerVariance).div(totalizerDifference).mul(100)).toFixed(2)
      : 0;

  const extractPumpNumber = (metadata) => {
    const match = metadata.match(/Pump (\d+)/);
    return match ? parseInt(match[1], 10) : null;
  };

  const getPumpTotalizer = ({ pump, productName = "", isStart }) => {
    productName = productName.replace(" ", "");
    return shiftPumpTotalsFields?.length > 0
      ? shiftPumpTotalsFields.find((totalizer) => totalizer.fuelingPointId === pump)?.[
          isStart ? `startTotalizer${productName}` : `endTotalizer${productName}`
        ]
      : "";
  };

  const getPumpTotalizerRemarks = (pump, productName = "") => {
    productName = productName.replace(" ", "");
    return shiftPumpTotalsFields?.length > 0
      ? shiftPumpTotalsFields.find((totalizer) => totalizer.fuelingPointId === pump)?.[
          `remarks${productName}`
        ]
      : "";
  };

  const isExist =
    transactions.length > 0 ? true : isProductExistInUGT(sku, shift.station.stationUgt);

  if (!isExist) {
    return <></>;
  }

  if (name === "Diesel") {
    console.log({
      productPumps,
    });
  }
  return (
    <>
      <AddRemarksTotalizerModal
        {...addRemarks}
        remarks={remarks}
        handleSave={(newRemarks) => {
          setRemarks(newRemarks);
          handleOnChangePumpRemarks({
            productName: selectedPump.productName,
            pump: selectedPump.pump,
            value: newRemarks,
          });
        }}
        pageMode={pageMode}
      />
      <div className={styles.fuelSummaryWrapper}>
        <div className={styles.tableHeader}>
          <Text className={styles.label}>{name}</Text>
        </div>
        <div className={styles.tableScrollContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                {fuelSalesColumn.map((item, i) => {
                  // if (!isShowRemarks && item.key === "remarks") {
                  //   return null;
                  // }
                  return <th key={i}>{item.text}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {productPumps.length !== 0 &&
                productPumps.map((item, i) => {
                  // const payment = getPaymentDetails(item.transactionItemId);
                  const totalizerDifference = item?.totalizerDifference || 0;
                  const totalizerVariance = item?.totalizerVariance || 0;
                  const totalizerVariancePercentage =
                    totalizerDifference && totalizerDifference !== 0
                      ? Number(Big(totalizerVariance).div(totalizerDifference).mul(100)).toFixed(2)
                      : 0;

                  const pumpPrice = getPrice(item.sku) ?? item.pumpPricePerItem ?? 0;
                  const itemPremium = item.computedPremium || 0;
                  const rowIsPremium = itemPremium > 0.005;
                  // const totalAmount = rowIsTaxVatExempt
                  //   ? computeTaxVatExemptFuelAmount(item, pumpPrice)
                  //   : rowIsPremium
                  //   ? pumpPremiumAmount(item)
                  //   : !item.isAmountInput && itemPumpDiscount > 0
                  //   ? item.netTotalAmount || 0
                  //   : item.isAmountInput && itemPumpDiscount > 0
                  //   ? item.totalDiscount === 0
                  //     ? item.netTotalAmount
                  //     : Big(item.originalTotalAmount || 0)
                  //         .minus(itemPumpDiscount)
                  //         .round(2)
                  //         .toNumber()
                  //   : item.originalTotalAmount || 0;

                  // const totalAmount = computeTotalAmount(item);
                  const totalAmount = item.computedTotalAmount;
                  // const adjustedAbsDiscount = Math.max(0, Math.abs(rawDiscount) - itemPumpDiscount);
                  // let totalDiscount =
                  //   item.discountValue < 0 ? -Math.abs(adjustedAbsDiscount) : adjustedAbsDiscount;

                  let totalDiscount = item.computedDiscount;

                  // const finalAmountPaid = item.netTotalAmount || 0;

                  const vatExemptAmount = item.vatExempt12percent;
                  const finalAmountPaid = totalAmount + totalDiscount + vatExemptAmount;

                  // const totalOtherDiscount = finalAmountPaid - totalAmount || 0;

                  let pumpNumber = 1;
                  if (item.metadata !== undefined) {
                    pumpNumber = extractPumpNumber(item.metadata);
                  }

                  const startTotalizer = getPumpTotalizer({
                    shiftPumpTotalId: item.shiftPumpTotalId,
                    productName: name,
                    isStart: true,
                    pump: item.pump,
                  });
                  const endTotalizer = getPumpTotalizer({
                    pump: item.pump,
                    productName: name,
                    isStart: false,
                  });
                  const totalizerRemarks = getPumpTotalizerRemarks(item.pump, name);

                  const totalizerProductName = name.replace(" ", "");
                  const isStartTotalizerIncomplete = startTotalizer === "";
                  const isEndTotalizerIncomplete =
                    endTotalizer === "" ||
                    endTotalizer === null ||
                    Big(removeComasStringAmount(startTotalizer)).gt(
                      removeComasStringAmount(endTotalizer),
                    );

                  return (
                    <tr key={i}>
                      <td>
                        {/* pump number */}
                        <Text className={styles.content}>{pumpNumber ?? "--"}</Text>
                      </td>
                      <td>
                        {/* pump price */}
                        <Text className={styles.content}>{formatAmount(pumpPrice)}</Text>
                      </td>
                      <td>
                        {/* pump discount */}
                        <Text className={styles.content}>
                          {formatAmount(item.pumpDiscountPerItem ?? 0)}
                        </Text>
                      </td>
                      <td
                        id={totalizerAnchorId(item.pump, totalizerProductName, true)}
                        data-incomplete={isStartTotalizerIncomplete}
                      >
                        {/* beginning totalizer */}
                        {[EmployeePosition.STATION_HEAD, "portal-admin"].includes(
                          accessRole?.role,
                        ) && pageMode === FormMode.Edit ? (
                          <InputAmount
                            value={startTotalizer}
                            onChange={(_, { value }) => {
                              handleOnChangePumpTotalizers({
                                productName: name,
                                pump: item.pump,
                                isStart: true,
                                value,
                              });
                            }}
                            prefix="L"
                            className={styles.amount}
                            innerLabel
                            decimalPlaces={3}
                            max={"999999999999999.999"}
                            min={0}
                            helperText={
                              <div
                                style={{
                                  color: "red",
                                  width: "170px",
                                  wordBreak: "break-word",
                                  whiteSpace: "normal",
                                  position: "relative",
                                }}
                              >
                                {startTotalizer === ""
                                  ? "Totalizer can’t be empty. Please enter a value."
                                  : ""}
                              </div>
                            }
                            error={isStartTotalizerIncomplete}
                            inputStyle={{
                              width: "175px",
                            }}
                            disabled={isEndingDipstickEnabled}
                            inputDisabled={isEndingDipstickEnabled}
                          />
                        ) : (
                          <Text className={styles.content}>
                            {startTotalizer === "" || startTotalizer == null || isTotalizerBlank
                              ? isShiftStarted
                                ? "N/A"
                                : ""
                              : formatVolume(startTotalizer ?? 0)}
                          </Text>
                        )}
                      </td>
                      <td
                        id={totalizerAnchorId(item.pump, totalizerProductName, false)}
                        data-incomplete={isEndTotalizerIncomplete}
                      >
                        {/* ending totalizer */}
                        {[EmployeePosition.STATION_HEAD, "portal-admin"].includes(
                          accessRole?.role,
                        ) && pageMode === FormMode.Edit ? (
                          <InputAmount
                            value={endTotalizer}
                            onChange={(_, { value }) => {
                              handleOnChangePumpTotalizers({
                                productName: name,
                                pump: item.pump,
                                isStart: false,
                                value,
                              });
                            }}
                            prefix="L"
                            className={styles.amount}
                            innerLabel
                            decimalPlaces={3}
                            max={"999999999999999.999"}
                            min={0}
                            helperText={
                              <div
                                style={{
                                  color: "red",
                                  width: "170px",
                                  wordBreak: "break-word",
                                  whiteSpace: "normal",
                                  position: "relative",
                                }}
                              >
                                {endTotalizer === ""
                                  ? "Totalizer can’t be empty. Please enter a value."
                                  : Big(removeComasStringAmount(startTotalizer)).gt(
                                        removeComasStringAmount(endTotalizer),
                                      )
                                    ? "Ending Totalizer Reading must not be less than Beginning."
                                    : ""}
                              </div>
                            }
                            error={isEndTotalizerIncomplete}
                            inputStyle={{
                              width: "175px",
                            }}
                            disabled={isEndingDipstickEnabled}
                            inputDisabled={isEndingDipstickEnabled}
                          />
                        ) : (
                          <Text className={styles.content}>
                            {isShiftStarted
                              ? "N/A"
                              : isTotalizerBlank ||
                                  [startTotalizer, endTotalizer].some(
                                    (totalizer) => totalizer === "" || totalizer == null,
                                  )
                                ? ""
                                : isShiftEndedCompletely
                                  ? formatVolume(endTotalizer)
                                  : "N/A"}
                          </Text>
                        )}
                      </td>
                      {/* <td> */}
                      {/* recorded volume */}
                      {/* <Text className={styles.content}>{formatVolume(item.quantity ?? 0)}</Text>
                      </td> */}
                      <td>
                        {/* amount */}
                        <Text className={styles.spacer100}>{formatAmount(totalAmount)}</Text>
                      </td>
                      <td>
                        {/* discount/premium */}
                        {(() => {
                          if (rowIsPremium && itemPremium > 0.005) {
                            const netPremium = itemPremium + (item.computedDiscountOnly ?? 0);
                            const isNetDiscount = netPremium < -0.005;
                            return (
                              <Text className={isNetDiscount ? styles.negative : styles.content}>
                                {isNetDiscount ? "-" : ""}
                                {formatAmount(Math.abs(netPremium))}
                              </Text>
                            );
                          }
                          const hasNonZeroDiscount = Math.abs(totalDiscount ?? 0) > 0.005;
                          return (
                            <Text className={hasNonZeroDiscount ? styles.negative : styles.content}>
                              {hasNonZeroDiscount ? "-" : ""}
                              {formatAmount(Math.abs(totalDiscount ?? 0))}
                            </Text>
                          );
                        })()}
                      </td>
                      <td>
                        {/* vat exempt */}
                        <Text className={vatExemptAmount < 0 ? styles.negative : styles.content}>
                          {vatExemptAmount < 0
                            ? `-${formatAmount(-vatExemptAmount)}`
                            : formatAmount(vatExemptAmount)}
                        </Text>
                      </td>
                      <td>
                        {/* final amount */}
                        <Text className={styles.spacer100}>
                          {formatAmount(finalAmountPaid ?? 0)}
                        </Text>
                      </td>
                      {/* {isShowRemarks && ( */}
                      <td>
                        {pageMode === FormMode.View ? (
                          <TooltipBubble content={totalizerRemarks} disabled={!totalizerRemarks}>
                            <Button
                              style={{
                                textDecoration: "none",
                                background: "none",
                                opacity: pageMode === FormMode.View && !totalizerRemarks ? 0.5 : 1,
                              }}
                              link
                            >
                              {totalizerRemarks ? locale.view : "-"}
                            </Button>
                          </TooltipBubble>
                        ) : (
                          <Button
                            style={{
                              textDecoration: "none",
                              background: "none",
                              opacity: pageMode === FormMode.View && !totalizerRemarks ? 0.5 : 1,
                            }}
                            link
                            onClick={() => {
                              if (pageMode === FormMode.View && !totalizerRemarks) {
                                return;
                              }

                              setRemarks(totalizerRemarks);
                              setSelectedPump({
                                productName: name,
                                pump: item.pump,
                              });
                              addRemarks.show();
                            }}
                          >
                            {totalizerRemarks ? locale.edit : locale.add}
                          </Button>
                        )}
                      </td>
                      {/* )} */}
                      {false && (
                        <>
                          <td>
                            {/* totalizer difference */}
                            <Text className={styles.content}>
                              {isShiftEndedCompletely
                                ? formatVolume(item.totalizerDifference ?? 0)
                                : "N/A"}
                            </Text>
                          </td>
                          <td>
                            {/* totalizer variance */}
                            <Text
                              className={classNames(
                                styles.content,
                                isShiftEndedCompletely && totalizerVariance > 0 && styles.positive,
                                isShiftEndedCompletely && totalizerVariance < 0 && styles.negative,
                              )}
                            >
                              {isShiftEndedCompletely
                                ? `${formatVolume(
                                    totalizerVariance ?? 0,
                                  )} (${totalizerVariancePercentage}%)`
                                : "N/A"}
                            </Text>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              {productPumps.length !== 0 && (
                <tr>
                  <td>
                    <Text className={styles.label}>{"Total"}</Text>
                  </td>
                  <td>
                    <Text className={styles.content}>{}</Text>
                  </td>
                  <td>
                    <Text className={styles.content}>{}</Text>
                  </td>
                  <td>
                    <Text className={styles.content}>{}</Text>
                  </td>
                  <td>
                    <Text className={styles.content}>{}</Text>
                  </td>
                  {/* <td>
                    <Text className={styles.label}>{formatVolume(totalSalesInLiters)}</Text>
                  </td> */}
                  <td>
                    <Text className={styles.label}>{formatAmount(totalAmount)}</Text>
                  </td>
                  <td>
                    {(() => {
                      if (totalPremium > 0.005) {
                        const netPremium = totalPremium + totalDiscountOnly;
                        const isNetDiscount = netPremium < -0.005;
                        return (
                          <Text className={isNetDiscount ? styles.negative : styles.content}>
                            {isNetDiscount ? "-" : ""}
                            {formatAmount(Math.abs(netPremium))}
                          </Text>
                        );
                      }
                      const hasNonZeroDiscount = Math.abs(totalDiscount ?? 0) > 0.005;
                      return (
                        <Text className={hasNonZeroDiscount ? styles.negative : styles.content}>
                          {hasNonZeroDiscount ? "-" : ""}
                          {formatAmount(Math.abs(totalDiscount ?? 0))}
                        </Text>
                      );
                    })()}
                  </td>
                  <td>
                    <Text className={totalVatExempt < 0 ? styles.negative : styles.label}>
                      {totalVatExempt < 0
                        ? `-${formatAmount(-totalVatExempt)}`
                        : formatAmount(totalVatExempt)}
                    </Text>
                  </td>
                  <td>
                    <Text className={styles.label}>{formatAmount(totalFinalAmount)}</Text>
                  </td>
                  <td></td>
                  {/* {isShowRemarks && <td></td>} */}
                  {false && (
                    <>
                      <td>
                        <Text className={styles.label}>
                          {isShiftEndedCompletely ? formatVolume(totalizerDifference) : "N/A"}
                        </Text>
                      </td>
                      <td>
                        <Text
                          className={classNames(
                            styles.content,
                            isShiftEndedCompletely && totalizerVariance > 0 && styles.positive,
                            isShiftEndedCompletely && totalizerVariance < 0 && styles.negative,
                          )}
                        >
                          {isShiftEndedCompletely
                            ? `${formatVolume(
                                totalizerVariance ?? 0,
                              )} (${totalizerVariancePercentage}%)`
                            : "N/A"}
                        </Text>
                      </td>
                    </>
                  )}
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {!productPumps.length && (
          <div className={styles.tableRow}>
            <Text className={styles.label}>{"No Results"}</Text>
          </div>
        )}
      </div>
    </>
  );
};

export default FuelSummary;
